/** Meeting-room charging as members and kiosks see it (billing phase 4).
 *
 *  The backend sends one ``pricing`` object per paid room (``billing.meeting_rooms.RoomPricing``); an
 *  unpaid room has ``pricing: null``. Its ``label`` is the server's full sentence; the helpers here derive
 *  the short, glanceable pieces (the price badge, the estimate, the terms) from the structured fields so
 *  every surface words them the same way. Amounts are SEK excl. VAT and display-only. */

export interface RoomPricing {
  paid: boolean;
  /** Booked or checked-in time, or a late-cancellation charge — such a room is booked in the member portal. */
  time_charged: boolean;
  charge_mode: "none" | "booked" | "checked_in";
  hourly_price_sek: number | null;
  late_cancel_hours: number | null;
  no_show_fee_sek: number | null;
  /** Server-built full sentence, e.g. "500 kr/h excl. VAT" or "Free until 1 Nov, then 500 kr/h excl. VAT". */
  label: string;
  /** Set while the room's price has not started yet (the room is free until then): the pricing as it
   *  will be from ``starts_on`` (ISO date). A booking on or after that date costs what ``upcoming`` says. */
  upcoming?: (RoomPricing & { starts_on: string; starts_at?: string | null }) | null;
}

/** Just what a badge needs, so consumers with a narrower type (e.g. a search candidate) can pass theirs. */
export type RoomPricingLike = Pick<RoomPricing, "paid" | "label"> & Partial<RoomPricing>;

/** The pricing that applies to a booking starting at ``startIso``: ``upcoming`` from its start on.
 *  Compared as instants against ``starts_at`` (midnight in the room's site time zone, from the backend),
 *  so a browser in another zone lands on the same side of the boundary as the billing engine; the
 *  browser-local date is only a fallback for payloads without it. */
export function pricingOn(pricing: RoomPricing | null | undefined, startIso: string): RoomPricing | null {
  if (!pricing) return null;
  const up = pricing.upcoming;
  if (!up) return pricing;
  const starts = up.starts_at
    ? new Date(startIso).getTime() >= new Date(up.starts_at).getTime()
    : localIsoDate(new Date(startIso)) >= up.starts_on;
  return starts ? up : pricing;
}

/** Shown as a badge: a paid room, or a free one whose price starts later. */
export function hasPriceInfo(pricing: RoomPricingLike | null | undefined): boolean {
  return !!pricing && (pricing.paid || !!pricing.upcoming);
}

function localIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "1,000" / "1,250.50" — thousands grouped with commas, decimals only when needed. */
export function formatKr(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  const [int, dec] = rounded.toFixed(2).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return Number.isInteger(rounded) ? grouped : `${grouped}.${dec}`;
}

/** Hours between two ISO instants, rounded to 2 decimals like the backend's charge quantity. */
export function bookingHours(startIso: string, endIso: string): number {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return Math.round((ms / 3_600_000) * 100) / 100;
}

function formatHours(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(2).replace(/0$/, "")} h`;
}

/** The short price for a badge: "500 kr/h excl. VAT", "No-show fee 100 kr", or the label as a fallback.
 *  ``null`` when the room is free. */
export function priceBadgeText(pricing: RoomPricingLike | null | undefined): string | null {
  if (!pricing) return null;
  if (!pricing.paid) {
    const up = pricing.upcoming;
    const next = up ? priceBadgeText(up) : null;
    return up && next ? `Free until ${formatDay(up.starts_on)}, then ${next}` : null;
  }
  if (pricing.charge_mode && pricing.charge_mode !== "none" && pricing.hourly_price_sek != null) {
    return `${formatKr(pricing.hourly_price_sek)} kr/h excl. VAT`;
  }
  if (pricing.charge_mode === "none" && pricing.no_show_fee_sek != null && pricing.late_cancel_hours == null) {
    return `No-show fee ${formatKr(pricing.no_show_fee_sek)} kr`;
  }
  return pricing.label || null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "1 Nov" from an ISO date — fixed English month names, like the invoice rows. */
export function formatDay(isoDate: string): string {
  const [, m, d] = isoDate.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}

export interface ChargeEstimate {
  /** Amount excl. VAT for the whole slot. */
  amount: number;
  hours: number;
  hourly: number;
  /** Checked-in time: the amount is a ceiling — only the time after check-in is charged. */
  upTo: boolean;
}

export function estimateCharge(
  pricing: RoomPricing | null | undefined,
  startIso: string,
  endIso: string,
): ChargeEstimate | null {
  pricing = pricingOn(pricing, startIso);
  if (!pricing?.paid || pricing.hourly_price_sek == null || pricing.charge_mode === "none") return null;
  const hours = bookingHours(startIso, endIso);
  if (!(hours > 0)) return null;
  return {
    amount: Math.round(hours * pricing.hourly_price_sek * 100) / 100,
    hours,
    hourly: pricing.hourly_price_sek,
    upTo: pricing.charge_mode === "checked_in",
  };
}

/** "2 h × 500 kr/h" */
export function estimateBreakdown(estimate: ChargeEstimate): string {
  return `${formatHours(estimate.hours)} × ${formatKr(estimate.hourly)} kr/h`;
}

/** One-line estimate, for places with room for a single sentence. */
export function estimateNote(
  pricing: RoomPricing | null | undefined,
  startIso: string,
  endIso: string,
): string | null {
  const est = estimateCharge(pricing, startIso, endIso);
  if (!est) return null;
  const amount = formatKr(est.amount);
  return est.upTo
    ? `Up to ${amount} kr excl. VAT: only checked-in time is charged.`
    : `Estimated charge: ${amount} kr excl. VAT, added to your next invoice.`;
}

/** The booking terms of a paid room, one short sentence each, in the order a member needs them. */
export function pricingTerms(pricing: RoomPricing | null | undefined): string[] {
  if (pricing && !pricing.paid && pricing.upcoming) {
    const up = pricing.upcoming;
    const from = formatDay(up.starts_on);
    const price = priceBadgeText(up);
    return [
      `Free to book until ${from}.`,
      ...(price ? [`From ${from}: ${price}.`] : []),
      ...pricingTerms(up).filter((t) => t !== "Free to book."),
    ];
  }
  if (!pricing?.paid) return [];
  const terms: string[] = [];
  if (pricing.charge_mode === "booked") terms.push("The booked time is charged, whether or not you use it.");
  if (pricing.charge_mode === "checked_in") terms.push("Only the time after you check in is charged.");
  if (pricing.charge_mode === "none" && pricing.late_cancel_hours == null) terms.push("Free to book.");
  if (pricing.late_cancel_hours != null) {
    terms.push(`Cancelling less than ${pricing.late_cancel_hours} h before the start is charged in full.`);
  }
  if (pricing.no_show_fee_sek != null) {
    terms.push(`Not checking in costs a no-show fee of ${formatKr(pricing.no_show_fee_sek)} kr excl. VAT.`);
  }
  if (pricing.charge_mode !== "none" || pricing.late_cancel_hours != null || pricing.no_show_fee_sek != null) {
    terms.push("Charges are added to your company's next invoice.");
  }
  return terms;
}

/** Warning for a cancellation inside the late-cancellation window; ``null`` outside it or without a rule. */
export function lateCancelNote(
  pricing: RoomPricing | null | undefined,
  startIso: string,
  now: Date = new Date(),
): string | null {
  pricing = pricingOn(pricing, startIso);
  if (!pricing || pricing.late_cancel_hours == null) return null;
  const deadline = new Date(startIso).getTime() - pricing.late_cancel_hours * 3_600_000;
  return now.getTime() > deadline
    ? `Cancelling less than ${pricing.late_cancel_hours} h before the start is charged in full.`
    : null;
}
